import type { DocumentOut, ProposalAttachmentOut } from "@/api";

/**
 * What the reading can read (`DOCUMENT_MIME` on the server) — the only
 * attachments the Eingang offers. Anything else stays in the record; adopting
 * it would be refused with a 422 anyway.
 */
const READABLE: Record<string, string> = {
  "application/pdf": "PDF",
  "image/jpeg": "JPEG",
  "image/png": "PNG",
};

/**
 * One drawing the Eingang offers to read:
 *
 * - an **attachment** of the mail — adopted on „Zeichnung einlesen" (a copy
 *   becomes a document, design 2026-09-22 §4.3), unless the same file is on
 *   the Vorgang already (`document`): dropped beside the mail, or adopted on
 *   an earlier visit; then that document is read and nothing is copied twice;
 * - a **document** of the Vorgang that is no attachment of the mail — as a
 *   rule dropped together with it on the start page; on file already, read
 *   as it is. Shown as „schon im Vorgang": a document does not say how it came.
 */
export type DrawingRow =
  | { key: string; kind: "attachment"; attachment: ProposalAttachmentOut; document: DocumentOut | null }
  | { key: string; kind: "document"; document: DocumentOut };

export interface Drawings {
  rows: DrawingRow[];
  /** Attachments the reading cannot read (an Excel, a Word file) — said, not offered. */
  others: number;
}

/** „PDF", „JPEG", „PNG" — `null` for a type the reading does not take. */
export function drawingType(mimeType: string): string | null {
  return READABLE[mimeType.trim().toLowerCase()] ?? null;
}

/**
 * The attachments and documents as one list, the mail's order first. A
 * document is matched to an attachment by name and size — what the server
 * stores is a copy, under the attachment's own name — and each document to
 * one attachment at most. Page sheets cut from a document (`derivedFrom`)
 * are no drawing of their own.
 */
export function drawingsOf(attachments: readonly ProposalAttachmentOut[], documents: readonly DocumentOut[]): Drawings {
  const unmatched = documents.filter((document) => document.derivedFrom === null);
  const rows: DrawingRow[] = [];
  let others = 0;
  for (const attachment of attachments) {
    if (drawingType(attachment.mimeType) === null) {
      others += 1;
      continue;
    }
    const at = unmatched.findIndex(
      (document) => document.originalFilename === attachment.filename && document.byteSize === attachment.byteSize,
    );
    const document = at === -1 ? null : unmatched.splice(at, 1)[0]!;
    rows.push({ key: `a:${attachment.id}`, kind: "attachment", attachment, document });
  }
  for (const document of unmatched) {
    if (drawingType(document.mimeType) === null) continue;
    rows.push({ key: `d:${document.id}`, kind: "document", document });
  }
  return { rows, others };
}

/** The drawing lies on the Vorgang already — „schon im Vorgang". */
export function onFile(row: DrawingRow): boolean {
  return row.kind === "document" || row.document !== null;
}

/**
 * Ticked until the person says otherwise: an attachment not on file yet that
 * the server thinks is a drawing (not a signature image, not a K60 document,
 * spec §3.1); a drawing on file where `tickOnFile` says so. That is the
 * Eingang's business (final review M-7, `lib/eingangArrival`): on the arrival
 * from the start page — dropped with the mail, to be read — and for a copy
 * made by a press that failed further on, yes; for what was on file when the
 * Eingang was opened again, no — it is read already as a rule, and a tick
 * would read it a second time by accident.
 */
export function tickedAtFirst(row: DrawingRow, tickOnFile: boolean): boolean {
  if (row.kind === "document" || row.document !== null) return tickOnFile;
  return row.attachment.drawingLikely;
}
